import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Activity,
  TrendingUp,
  Users,
  CheckCircle2,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Target,
  Sparkles,
  BarChart3
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

// Softened, pale color palette
export const PALE_COLORS = {
  // Softened semantic colors - pale versions
  positive: '#86EFAC',      // Soft green (was #10B981)
  negative: '#FCA5A5',      // Soft red (was #EF4444)
  neutral: '#94A3B8',       // Soft slate
  warning: '#FCD34D',       // Soft amber
  
  // Pale gradient for sentiment scales
  sentiment_5: ['#FCA5A5', '#FCD34D', '#94A3B8', '#86EFAC', '#4ADE80'],
  sentiment_7: ['#F87171', '#FCA5A5', '#FCD34D', '#94A3B8', '#A7F3D0', '#86EFAC', '#4ADE80'],
  
  // Pale binary
  binary_positive_negative: ['#86EFAC', '#FCA5A5'],
  binary_with_neutral: ['#86EFAC', '#FCA5A5', '#94A3B8'],
  
  // Generic palette - pale and uniform
  generic: ['#93C5FD', '#86EFAC', '#FCD34D', '#C4B5FD', '#FCA5A5', '#67E8F9', '#A7F3D0', '#F9A8D4', '#5EEAD4']
};

// Data Quality Indicator Component - Now Collapsible
export function DataQualityIndicator({ aggregation, population }) {
  const [isOpen, setIsOpen] = useState(false);
  
  if (!aggregation || !population) return null;

  const totalResponses = aggregation.total_responses || 0;
  const entropy = aggregation.entropy || 0;
  const avgConfidence = aggregation.avg_confidence ? (aggregation.avg_confidence * 100).toFixed(1) : null;

  // Calculate consensus score (inverse of diversity - higher means more agreement)
  const distributionData = aggregation.distribution || aggregation.sentiment_distribution || {};
  const maxEntropy = Math.log2(Object.keys(distributionData).length || 5);
  const consensusScore = maxEntropy > 0 ? Math.round((1 - entropy / maxEntropy) * 100) : 50;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border border-slate-200/80 bg-slate-50/30">
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50/50 transition-colors rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-slate-500" />
                <CardTitle className="text-sm font-medium text-slate-600">
                  Response Metrics
                </CardTitle>
                <Badge variant="outline" className="text-xs text-slate-500 bg-slate-50 border-slate-200">
                  N = {totalResponses}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                {isOpen ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Click to {isOpen ? 'hide' : 'view'} detailed response metrics
            </p>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-4 pt-2">
            {/* Compact metrics grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Consensus Score */}
              <div className="p-3 bg-white/80 rounded-lg border border-slate-100">
                <div className="flex items-center gap-1.5 mb-1">
                  <Target className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs font-medium text-slate-600">Consensus</span>
                  <HelpCircle className="w-3 h-3 text-slate-300 cursor-help" title="How much respondents agree (higher = more consensus)" />
                </div>
                <p className="text-lg font-bold text-slate-800">{consensusScore}%</p>
                <p className="text-[10px] text-slate-500">
                  {consensusScore > 70 ? 'High agreement' : consensusScore > 40 ? 'Mixed views' : 'Diverse opinions'}
                </p>
              </div>

              {/* Response Diversity (Entropy) */}
              <div className="p-3 bg-white/80 rounded-lg border border-slate-100">
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingUp className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs font-medium text-slate-600">Diversity</span>
                  <HelpCircle className="w-3 h-3 text-slate-300 cursor-help" title="Shannon entropy - higher means more varied responses" />
                </div>
                <p className="text-lg font-bold text-slate-800">{entropy.toFixed(2)}</p>
                <p className="text-[10px] text-slate-500">
                  {entropy > 1.5 ? 'High variation' : entropy > 1.0 ? 'Moderate' : 'Low variation'}
                </p>
              </div>

              {/* Average Confidence */}
              <div className="p-3 bg-white/80 rounded-lg border border-slate-100">
                <div className="flex items-center gap-1.5 mb-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-xs font-medium text-slate-600">Model Certainty</span>
                </div>
                <p className="text-lg font-bold text-slate-800">{avgConfidence || 'N/A'}%</p>
                <p className="text-[10px] text-slate-500">Avg agent certainty</p>
              </div>

              {/* Sample Size */}
              <div className="p-3 bg-white/80 rounded-lg border border-slate-100">
                <div className="flex items-center gap-1.5 mb-1">
                  <Users className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-xs font-medium text-slate-600">Sample</span>
                </div>
                <p className="text-lg font-bold text-slate-800">{totalResponses}</p>
                <p className="text-[10px] text-slate-500">Total responses</p>
              </div>
            </div>

            {/* Info note */}
            <div className="flex items-start gap-2 p-2 bg-blue-50/50 rounded-lg border border-blue-100/50">
              <Info className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" />
              <p className="text-[11px] text-blue-700/80 leading-relaxed">
                <strong>About these metrics:</strong> These describe the shape of your data — not whether results are reliable. 
                Consensus measures agreement level. Diversity (entropy) measures response spread. 
                For result reliability and confidence, see the <strong>Reliability</strong> tab.
              </p>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Response Heatmap Component - Enhanced with deviation-based contrast
export function ResponseHeatmap({ breakdown, responseScale, groupBy, aggregation }) {
  if (!breakdown || !breakdown.breakdown || !responseScale) return null;

  const groups = Object.keys(breakdown.breakdown);
  const overallPcts = aggregation?.percentages || {};
  
  // Deviation-based intensity colors
  const getIntensityColor = (percentage, response) => {
    const overallPct = overallPcts[response] || 0;
    const deviation = Math.abs(percentage - overallPct);
    
    if (deviation > 12) return 'bg-red-200';
    if (deviation > 8) return 'bg-amber-200';
    if (percentage >= 50) return 'bg-emerald-200';
    if (percentage >= 35) return 'bg-emerald-100';
    if (percentage >= 25) return 'bg-amber-100';
    if (percentage >= 15) return 'bg-slate-100';
    return 'bg-slate-50';
  };

  const getTextColor = (percentage, response) => {
    const overallPct = overallPcts[response] || 0;
    const deviation = Math.abs(percentage - overallPct);
    
    if (deviation > 12) return 'text-red-800';
    if (deviation > 8) return 'text-amber-800';
    if (percentage >= 50) return 'text-emerald-800';
    if (percentage >= 35) return 'text-emerald-700';
    if (percentage >= 25) return 'text-amber-700';
    return 'text-slate-600';
  };

  const getDeviation = (percentage, response) => {
    const overallPct = overallPcts[response] || 0;
    return Math.abs(percentage - overallPct);
  };

  // Compute segment sizes
  const totalN = aggregation?.total_responses || 1;

  return (
    <Card className="border border-slate-200/80 chart-animate">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-slate-500" />
          Response Heatmap
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Response distribution across {groupBy} groups — cells with significant deviation from baseline are highlighted
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left p-2 text-xs font-semibold text-slate-600">{groupBy}</th>
                {responseScale.map((response, idx) => (
                  <th key={idx} className="text-center p-2 text-xs font-semibold text-slate-600 min-w-[80px]">
                    {response}
                  </th>
                ))}
                <th className="text-center p-2 text-xs font-semibold text-slate-400 min-w-[60px]">N (%)</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group, groupIdx) => {
                const data = breakdown.breakdown[group];
                const segCount = data.count || 0;
                const segPct = ((segCount / totalN) * 100).toFixed(0);
                return (
                  <tr key={groupIdx} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="p-2 text-xs font-medium text-slate-700">{group}</td>
                    {responseScale.map((response, respIdx) => {
                      const pct = data.percentages?.[response] || 0;
                      const dev = getDeviation(pct, response);
                      return (
                        <td key={respIdx} className="p-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div 
                                  className={`rounded px-2 py-1 text-center text-xs font-semibold transition-all hover:scale-102 ${getIntensityColor(pct, response)} ${getTextColor(pct, response)} relative`}
                                >
                                  {pct.toFixed(0)}%
                                  {dev > 12 && (
                                    <span className="absolute -top-1 -right-1 text-[8px] text-red-600 font-black">!</span>
                                  )}
                                </div>
                              </TooltipTrigger>
                              {dev > 8 && (
                                <TooltipContent side="top" className="text-xs">
                                  Significant deviation from overall baseline ({dev > 0 ? '+' : ''}{(pct - (overallPcts[response] || 0)).toFixed(1)}pp)
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                      );
                    })}
                    <td className="p-1 text-center text-[10px] text-slate-400 font-medium">{segCount} ({segPct}%)</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Legend */}
        <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
          <span className="font-medium">Intensity:</span>
          <div className="flex items-center gap-1">
            <div className="w-6 h-4 bg-slate-100 rounded border border-slate-200"></div>
            <span>Low</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-4 bg-amber-100 rounded border border-amber-200"></div>
            <span>Med</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-4 bg-emerald-200 rounded border border-emerald-300"></div>
            <span>High</span>
          </div>
          <span className="mx-1 text-slate-300">|</span>
          <div className="flex items-center gap-1">
            <div className="w-6 h-4 bg-amber-200 rounded border border-amber-300"></div>
            <span>Dev &gt;8pp</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-4 bg-red-200 rounded border border-red-300 relative">
              <span className="absolute -top-1 -right-1 text-[7px] text-red-600 font-black">!</span>
            </div>
            <span>Dev &gt;12pp</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Summary Statistics Card - Simplified with pale colors
export function SummaryStatistics({ aggregation, population }) {
  if (!aggregation) return null;

  const entropy = aggregation.entropy || 0;
  // For open-ended, use sentiment_distribution; for closed, use distribution
  const distributionData = aggregation.distribution || aggregation.sentiment_distribution || {};
  const maxEntropy = Math.log2(Object.keys(distributionData).length || 5);
  const consensusScore = maxEntropy > 0 ? Math.round((1 - entropy / maxEntropy) * 100) : 50;

  const stats = [
    {
      label: 'Total Responses',
      value: aggregation.total_responses || 0,
      icon: Users,
      color: 'text-blue-600 bg-blue-50/80'
    },
    {
      label: 'Consensus Score',
      value: `${consensusScore}%`,
      icon: Target,
      color: 'text-emerald-600 bg-emerald-50/80',
      subtitle: consensusScore > 70 ? 'High agreement' : consensusScore > 40 ? 'Mixed' : 'Diverse'
    },
    {
      label: 'Avg Confidence',
      value: aggregation.avg_confidence ? `${(aggregation.avg_confidence * 100).toFixed(0)}%` : 'N/A',
      icon: CheckCircle2,
      color: 'text-blue-600 bg-blue-50/80'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {stats.map((stat, idx) => (
        <Card key={idx} className="border border-slate-200/80 card-entrance hover:shadow-sm transition-all" style={{ animationDelay: `${idx * 0.1}s` }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-slate-500 mb-1">{stat.label}</p>
                <p className="text-xl font-bold text-slate-800 stat-number">{stat.value}</p>
                {stat.subtitle && (
                  <p className="text-[11px] text-slate-500 mt-0.5">{stat.subtitle}</p>
                )}
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Demographic Filter Component - To be used inside breakdown sections
export function DemographicFilter({ 
  population, 
  filterField, 
  filterValue, 
  onFilterFieldChange, 
  onFilterValueChange, 
  onClear,
  compact = false 
}) {
  if (!population?.columns) return null;

  // Helper to order demographic values
  const getOrderedValues = (values, columnName) => {
    if (!values || values.length === 0) return [];
    return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={filterField || ''}
          onChange={(e) => { onFilterFieldChange(e.target.value); onFilterValueChange(''); }}
          className="text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
        >
          <option value="">All groups</option>
          {population.columns.map(col => (
            <option key={col.name} value={col.name}>{col.name}</option>
          ))}
        </select>
        {filterField && (
          <select
            value={filterValue}
            onChange={(e) => onFilterValueChange(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
          >
            <option value="">Select value</option>
            {getOrderedValues(
              population.columns.find(c => c.name === filterField)?.sample_values || [],
              filterField
            ).map(val => (
              <option key={val} value={val}>{val}</option>
            ))}
          </select>
        )}
        {(filterField || filterValue) && (
          <button 
            onClick={onClear}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >
            Clear
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-end gap-3 flex-wrap p-3 bg-slate-50/50 rounded-lg border border-slate-100">
      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-600">Filter by</label>
        <select
          value={filterField || ''}
          onChange={(e) => { onFilterFieldChange(e.target.value); onFilterValueChange(''); }}
          className="text-sm px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
        >
          <option value="">All</option>
          {population.columns.map(col => (
            <option key={col.name} value={col.name}>{col.name}</option>
          ))}
        </select>
      </div>
      {filterField && (
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600">Value</label>
          <select
            value={filterValue}
            onChange={(e) => onFilterValueChange(e.target.value)}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">Select...</option>
            {getOrderedValues(
              population.columns.find(c => c.name === filterField)?.sample_values || [],
              filterField
            ).map(val => (
              <option key={val} value={val}>{val}</option>
            ))}
          </select>
        </div>
      )}
      {(filterField || filterValue) && (
        <button 
          onClick={onClear}
          className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1.5"
        >
          Clear filter
        </button>
      )}
    </div>
  );
}


// Simulation Quality Panel - V2.5 Upgrade
export function SimulationQualityPanel({ simulation, aggregation, stimulus, population }) {
  const [isOpen, setIsOpen] = useState(false);
  
  if (!simulation || !aggregation) return null;

  const totalResponses = aggregation.total_responses || 0;
  const entropy = aggregation.entropy || 0;
  const avgConfidence = aggregation.avg_confidence ? (aggregation.avg_confidence * 100).toFixed(0) : null;
  const preMode = simulation.pre_mode_used !== undefined ? simulation.pre_mode_used : (aggregation.output_mode === 'probs');
  const isOpenEnded = stimulus?.stimulus_type === 'open_text';

  // Calculate entropy label
  const getEntropyLabel = (e) => {
    if (e > 2) return 'High';
    if (e > 1) return 'Medium';
    return 'Low';
  };

  // Calculate consensus score (inverse of diversity)
  // For open-ended, use sentiment_distribution; for closed, use distribution
  const distributionData = aggregation.distribution || aggregation.sentiment_distribution || {};
  const maxEntropy = Math.log2(Object.keys(distributionData).length || 5);
  const consensusScore = maxEntropy > 0 ? Math.round((1 - entropy / maxEntropy) * 100) : 50;

  // Actionable diagnostics - V2.5 Phase 3
  const getActionableDiagnostics = () => {
    const diagnostics = [];
    
    if (!isOpenEnded) {
      // High entropy + low consensus — note this neutrally
      if (entropy > 1.5 && consensusScore < 40) {
        diagnostics.push({
          type: 'info',
          icon: Info,
          title: 'Highly diverse responses',
          message: 'Responses are spread across options with no dominant view. Check demographic segments for hidden patterns.',
          action: 'View Demographic Breakdown'
        });
      }
      
      // Low entropy + high consensus
      if (entropy < 0.8 && consensusScore > 80) {
        diagnostics.push({
          type: 'info',
          icon: Info,
          title: 'Very high agreement',
          message: 'Most respondents converge on the same answer. Verify question wording and population diversity.',
          action: 'Review Question & Population'
        });
      }
      
      // Low confidence
      if (avgConfidence && avgConfidence < 50) {
        diagnostics.push({
          type: 'warning',
          icon: AlertCircle,
          title: 'Low model certainty',
          message: 'Agent uncertainty is high. Consider adding calibration data or refining demographics.',
          action: 'Add Pretrain Data'
        });
      }
    }
    
    // Sample size warnings
    if (totalResponses < 100) {
      diagnostics.push({
        type: 'warning',
        icon: AlertCircle,
        title: 'Small sample size',
        message: 'Consider increasing population size for more robust insights.',
        action: 'Increase Sample Size'
      });
    }
    
    return diagnostics;
  };

  const diagnostics = getActionableDiagnostics();

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border border-slate-200">
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                <CardTitle className="text-base font-semibold text-slate-800">Response Profile</CardTitle>
                
                {/* Mode Badge */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className={cn(
                        "text-xs gap-1.5",
                        preMode ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-700"
                      )}>
                        {preMode ? <Sparkles className="w-3 h-3" /> : <BarChart3 className="w-3 h-3" />}
                        {preMode ? 'PRE' : 'Standard'}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p className="text-xs">
                        {preMode 
                          ? "PRE samples responses from probability distributions for each agent, enabling advanced analytics."
                          : "Standard mode: Direct response generation without probability distributions."}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Entropy Badge - Only for closed-ended */}
                {!isOpenEnded && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-xs bg-slate-50">
                          Entropy: {entropy.toFixed(2)} <span className="ml-1 text-[10px]">({getEntropyLabel(entropy)})</span>
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs">
                        <p className="text-xs">
                          Measures response diversity. Higher entropy means wider spread of opinions.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {/* Confidence Badge */}
                {avgConfidence && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                          Avg Confidence: {avgConfidence}%
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs">
                        <p className="text-xs">
                          Average certainty in agent response estimation.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {/* Sample Size Badge */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="text-xs bg-slate-50">
                        <Users className="w-3 h-3 mr-1" />
                        N = {totalResponses}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p className="text-xs">
                        Total simulated responses.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 flex-shrink-0">
                <ChevronDown className={cn(
                  "w-4 h-4 text-slate-400 transition-transform",
                  isOpen && "rotate-180"
                )} />
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              Click to {isOpen ? 'hide' : 'view'} detailed quality metrics
            </p>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-4 border-t border-slate-100">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              {/* Consensus Score */}
              {!isOpenEnded && (
                <div className="p-3 rounded-lg bg-emerald-50/50 border border-emerald-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-medium text-emerald-800">Consensus Score</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">{consensusScore}%</p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    {consensusScore > 70 ? 'High agreement' : consensusScore > 40 ? 'Mixed opinions' : 'Diverse views'}
                  </p>
                </div>
              )}

              {/* Diversity Score */}
              {!isOpenEnded && (
                <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-800">Diversity</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">{getEntropyLabel(entropy)}</p>
                  <p className="text-xs text-blue-600 mt-0.5">
                    Entropy: {entropy.toFixed(3)}
                  </p>
                </div>
              )}

              {/* Confidence Score */}
              {avgConfidence && (
                <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-800">Confidence</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">{avgConfidence}%</p>
                  <p className="text-xs text-blue-600 mt-0.5">
                    {avgConfidence > 70 ? 'High certainty' : avgConfidence > 50 ? 'Moderate' : 'Low certainty'}
                  </p>
                </div>
              )}
            </div>

            {/* What does this mean? */}
            <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-700 mb-1">What does this mean?</p>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {isOpenEnded ? (
                      <>Open-ended responses provide qualitative insights. Review themes, sentiment, and individual comments for deeper understanding.</>
                    ) : (
                      <>These metrics describe the shape of your response data — consensus, spread, and model certainty. For a full reliability assessment, see the <strong>Reliability</strong> tab.</>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Actionable Diagnostics - Phase 3 */}
            {diagnostics.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <Target className="w-3.5 h-3.5" />
                  Actionable Insights
                </p>
                {diagnostics.map((diagnostic, idx) => {
                  const Icon = diagnostic.icon;
                  const colorClasses = {
                    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                    warning: 'bg-amber-50 border-amber-200 text-amber-800',
                    info: 'bg-blue-50 border-blue-200 text-blue-800'
                  };
                  
                  return (
                    <div key={idx} className={`p-2.5 rounded-lg border ${colorClasses[diagnostic.type]}`}>
                      <div className="flex items-start gap-2">
                        <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium mb-0.5">{diagnostic.title}</p>
                          <p className="text-xs opacity-90 leading-relaxed">{diagnostic.message}</p>
                          {diagnostic.action && (
                            <button className="text-xs font-medium underline mt-1.5 hover:opacity-70">
                              {diagnostic.action} →
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

